import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, TrendingUp, TrendingDown, Minus, Send, RefreshCw, AlertCircle } from "lucide-react";

const _fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
};

const _scoreBadge = (s) => {
  const cfg = {
    1: { color: "#c0392b", label: "1/5", icon: "🚨" },
    2: { color: "#e67e22", label: "2/5", icon: "⚠️" },
    3: { color: "#888",    label: "3/5", icon: "😐" },
  }[s] || { color: "#888", label: `${s}/5`, icon: "•" };
  return (
    <span
      data-testid={`nps-score-badge-${s}`}
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        background: `${cfg.color}1A`,
        color: cfg.color,
        fontSize: "12px",
        fontWeight: 600,
      }}
    >
      {cfg.icon} {cfg.label}
    </span>
  );
};

const TrendPill = ({ delta }) => {
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: "#c0392b1A", color: "#c0392b" }}
            data-testid="nps-trend-up">
        <TrendingUp className="w-3 h-3" /> +{delta} vs prior 24h
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: "#27ae601A", color: "#27ae60" }}
            data-testid="nps-trend-down">
        <TrendingDown className="w-3 h-3" /> {delta} vs prior 24h
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
          style={{ background: "#8881", color: "#888" }}
          data-testid="nps-trend-flat">
      <Minus className="w-3 h-3" /> flat
    </span>
  );
};

const Sparkline = ({ buckets, labels }) => {
  const max = Math.max(1, ...buckets);
  return (
    <div className="flex items-end gap-2" data-testid="nps-sparkline" style={{ height: 88 }}>
      {buckets.map((n, i) => {
        const h = Math.max(n > 0 ? 4 : 0, Math.round((n / max) * 64));
        const isToday = i === 6;
        const day = labels[i] ? new Date(labels[i]).toLocaleDateString(undefined, { weekday: "short" }) : "";
        return (
          <div key={i} className="flex flex-col items-center" style={{ width: 40 }}>
            <div className="text-xs text-muted-foreground" style={{ fontWeight: n > 0 ? 600 : 400 }}>{n}</div>
            <div
              className="rounded-t mt-1"
              style={{
                height: `${h}px`,
                width: "100%",
                background: isToday ? "#FF2D7A" : "#FFB42D",
                minHeight: n > 0 ? "4px" : "0",
                opacity: n > 0 ? 1 : 0.25,
              }}
            />
            <div className="text-[10px] text-muted-foreground mt-1">{day}</div>
          </div>
        );
      })}
    </div>
  );
};

const AdminPlyxNps = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/plyx/nps-stats");
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Forbidden");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    load();
  }, [user, navigate]);

  const triggerDigest = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/plyx/send-low-nps-digest-now");
      alert(`Digest sent — ${r.data.rows_sent} row(s) included`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="container-narrow py-20 text-center text-muted-foreground">
        <AlertCircle className="w-8 h-8 mx-auto mb-4 opacity-60" />
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="container-narrow py-20 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }

  const { last_24h, prior_24h, delta, week_buckets, week_labels, week_total, by_score_30d, by_score_30d_total, recent, generated_at } = data;

  return (
    <div className="container-narrow py-10" data-testid="admin-plyx-nps-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Plyx NPS</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live detractor signal — same data as the daily digest email, on demand.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/plyx"
            data-testid="nps-link-to-overview"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5"
          >
            ← Overview
          </Link>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="nps-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={triggerDigest}
            disabled={sending}
            data-testid="nps-send-digest-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {sending ? "Sending…" : "Send digest now"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-black/10 rounded-xl p-6" data-testid="nps-tile-24h">
          <div className="label-overline text-muted-foreground">Last 24h</div>
          <div className="font-display text-4xl mt-3 tabular-nums">{last_24h}</div>
          <div className="mt-2"><TrendPill delta={delta} /></div>
          <div className="text-xs text-muted-foreground mt-2">Day before: <strong>{prior_24h}</strong></div>
        </div>
        <div className="bg-white border border-black/10 rounded-xl p-6" data-testid="nps-tile-week">
          <div className="label-overline text-muted-foreground">Last 7 days</div>
          <div className="font-display text-4xl mt-3 tabular-nums">{week_total}</div>
          <div className="mt-3">
            <Sparkline buckets={week_buckets} labels={week_labels} />
          </div>
        </div>
        <div className="bg-white border border-black/10 rounded-xl p-6" data-testid="nps-tile-30d">
          <div className="label-overline text-muted-foreground">Last 30 days by score</div>
          <div className="font-display text-4xl mt-3 tabular-nums">{by_score_30d_total}</div>
          <div className="flex gap-3 mt-3 text-sm">
            <div>🚨 <strong>{by_score_30d["1"] || 0}</strong> <span className="text-muted-foreground">1/5</span></div>
            <div>⚠️ <strong>{by_score_30d["2"] || 0}</strong> <span className="text-muted-foreground">2/5</span></div>
            <div>😐 <strong>{by_score_30d["3"] || 0}</strong> <span className="text-muted-foreground">3/5</span></div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-black/10 rounded-xl overflow-hidden" data-testid="nps-recent-table">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <h2 className="font-semibold">Recent detractors</h2>
          <span className="text-xs text-muted-foreground">{recent.length} most-recent rows</span>
        </div>
        {recent.length === 0 ? (
          <div className="px-6 py-10 text-center text-muted-foreground text-sm">
            No detractors yet — that&rsquo;s a good thing.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/5">
                <tr>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground" style={{ width: 80 }}>Score</th>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">Reason</th>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground" style={{ width: 180 }}>Referrer</th>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground" style={{ width: 130 }}>When</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.feedback_id} className="border-t border-black/5" data-testid={`nps-row-${r.feedback_id}`}>
                    <td className="px-4 py-3">{_scoreBadge(r.score)}</td>
                    <td className="px-4 py-3">{r.reason || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground truncate" style={{ maxWidth: 180 }}>{r.referrer || "—"}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{_fmtWhen(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Generated at {_fmtWhen(generated_at)} · Tap “Refresh” for the latest numbers.
      </p>
    </div>
  );
};

export default AdminPlyxNps;
